// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file FieldLabel.tsx
 * @input Uses React, Icon, IconType, useTranslator
 * @output Exports FieldLabel component, FieldLabelProps
 * @position Core label implementation; used by Field, CheckboxInput, Switch
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Field/Field.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/Field/index.ts (exports if types change)
 * - /packages/cli/assets/templates/blocks/components/Field/ (showcase blocks)
 * - /packages/core/locales/en.json (@astryx.field.required / @astryx.field.optional)
 */
import { useMemo, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import { mergeProps } from "../utils/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, fontWeightVars, spacingVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { Tooltip } from "../Tooltip/index.js";
import { useTranslator } from "../i18n/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useInputContainer } from "../hooks/index.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  label: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "xzye2dw",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib",
    kLWn49: "x1kq96og",
    k63SB2: "x1e4wzip",
    kMwMTN: "xv1l7n4",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  labelDisabled: {
    kMwMTN: "xnbbluu",
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  srOnly: {
    ksu8eU: "xng3xce",
    kMcinP: "xzpqnlu",
    kZKoxP: "xjm9jq1",
    kLqNvP: "x1o0tod",
    kogj98: "xkdpibf",
    kVQacm: "xb3r6kr",
    kmVPX3: "x1717udv",
    kfzvcC: "x47corl",
    kVAEAm: "x10l6tqk",
    k87sOh: "x13vifvy",
    kfSwDN: "x87ps6o",
    khDVqt: "xuxw1ft",
    kzqmXN: "x1i1rx1s",
    $$css: true
  }
};
/**
 * Label + description group for form fields. Handles sr-only hiding,
 * disabled styling, optional/required indicators, icons, and tooltips.
 *
 * When `isLabelHidden` is true the entire group uses sr-only positioning
 * so it takes up zero layout space — no wrapper div left in flow.
 *
 * @example
 * ```
 * <FieldLabel label="Email" inputID={inputId} description="We won't share it" />
 * <FieldLabel label="Search" inputID={inputId} isLabelHidden />
 * ```
 */
export function FieldLabel({
  label,
  inputID,
  labelID,
  isGroupLabel = false,
  isLabelHidden = false,
  isDisabled = false,
  isOptional = false,
  isRequired = false,
  labelIcon,
  labelTooltip,
  description,
  descriptionID,
  className,
  style,
  xstyle,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const statusText = isOptional ? t('@astryx.field.optional') : isRequired ? t('@astryx.field.required') : null;

  // A group label (e.g. for a radiogroup) must not be a literal `<label>`
  // element: a `<label>` semantically names a single form control and can't be
  // associated with a group. Render it as a `<span>` instead, keeping all the
  // label styling and slots. The group references it via `aria-labelledby`.
  const LabelElement = isGroupLabel ? 'span' : 'label';

  // Clicking the description forwards to the associated control, so the whole
  // label area (label text + description) is one hit target — mirroring native
  // `<label>` click behavior, which the description can't get from `htmlFor`
  // because it stays a sibling `<span>` (nesting it in the `<label>` would fold
  // it into the control's accessible name and double-announce it alongside
  // `aria-describedby`). A group label names a group, not one control, so it
  // has nothing to forward to.
  const forwardsDescriptionClick = !isGroupLabel && inputID != null;

  // Reuse `useInputContainer` — the same hook input wrappers use to forward
  // clicks on non-interactive chrome to their control. It skips nested
  // interactive content (a link/button inside a ReactNode description) and
  // guards against forwarding during text selection, so the description needs
  // no bespoke click logic. The "input" is resolved from `inputID` lazily via
  // a ref-shaped object, since FieldLabel points at the control by id rather
  // than holding a ref to it.
  const descriptionRef = useRef(null);
  const controlRef = useMemo(() => ({
    get current() {
      return inputID == null ? null : descriptionRef.current?.ownerDocument.getElementById(inputID) ?? null;
    },
    set current(_value) {}
  }), [inputID]);
  const descriptionClickProps = useInputContainer({
    containerRef: descriptionRef,
    inputRef: controlRef,
    disabled: !forwardsDescriptionClick
  });
  const labelContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [labelIcon && renderIconSlot(labelIcon, {
      size: 'sm',
      color: 'inherit'
    }), label, statusText && /*#__PURE__*/_jsxs("span", {
      ...{
        className: "x1sodnla x141an7d x1ltkj2j xv1l7n4"
      },
      children: [/*#__PURE__*/_jsx("span", {
        "aria-hidden": "true",
        children: " \u2219 "
      }), statusText]
    }), labelTooltip && /*#__PURE__*/_jsx(Tooltip, {
      content: labelTooltip,
      placement: "above",
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "info",
        size: "sm",
        color: "inherit"
      })
    })]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(LabelElement, {
      ref: ref,
      id: labelID
      // `htmlFor` only applies to a real `<label>` associating with a single
      // control; a group label (span) has no `htmlFor`.
      ,
      htmlFor: isGroupLabel ? undefined : inputID,
      ...rest,
      ...mergeProps(themeProps('field-label'), stylex.props(styles.label, isDisabled && styles.labelDisabled, isLabelHidden && styles.srOnly, xstyle), className, style),
      children: labelContent
    }), description && /*#__PURE__*/_jsx("span", {
      ref: forwardsDescriptionClick ? descriptionRef : undefined,
      id: descriptionID,
      ...(forwardsDescriptionClick ? descriptionClickProps : undefined),
      ...{
        0: {
          className: "x9ynric x141an7d x1ltkj2j x1sodnla xv1l7n4"
        },
        2: {
          className: "x9ynric x141an7d x1ltkj2j x1sodnla xv1l7n4 x1ypdohk"
        },
        1: {
          className: "x9ynric x141an7d x1ltkj2j x1sodnla xv1l7n4 xng3xce xzpqnlu xjm9jq1 x1o0tod xkdpibf xb3r6kr x1717udv x47corl x10l6tqk x13vifvy x87ps6o xuxw1ft x1i1rx1s"
        },
        3: {
          className: "x9ynric x141an7d x1ltkj2j x1sodnla xv1l7n4 x1ypdohk xng3xce xzpqnlu xjm9jq1 x1o0tod xkdpibf xb3r6kr x1717udv x47corl x10l6tqk x13vifvy x87ps6o xuxw1ft x1i1rx1s"
        }
      }[!!forwardsDescriptionClick << 1 | !!isLabelHidden << 0],
      children: description
    })]
  });
}
FieldLabel.displayName = 'FieldLabel';